import React, { useState } from 'react';

import {
  Badge,
  Button,
  Row,
  Divider,
  Typography,
  Col,
  Space,
  List,
  Skeleton,
  Empty,
  Spin,
  Tooltip,
} from 'antd';
import StatCard from '@components/StatCard';
import useSwr from '@hooks/useSwr';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined } from '@ant-design/icons';
import CustomCard from '@components/CustomCard';
import { ContainerRow } from './styles';

const { Title } = Typography;

interface ITBDetails {
  customerId: number | null;
  msisdn: string;
  imsi: string;
}

const TabPanesAdvanced: React.FC<ITBDetails> = ({
  customerId,
  msisdn,
  imsi,
}) => {
  const [ableToGet, setAbletoGet] = useState(true);

  const { t } = useTranslation('inventory-detailed');

  const { data: subscriberInfo, mutate } = useSwr(
    '/service-proxy/sims/subscriber-infos',
    {
      method: 'get',
      params: {
        customerId,
        ...(imsi ? { imsi } : { msisdn }),
      },
    },
  );

  const handleTest = () => {
    setAbletoGet(false);
    mutate();
    setTimeout(() => {
      setAbletoGet(true);
    }, 1000);
  };

  return (
    <ContainerRow gutter={[12, 12]}>
      <Col lg={24} xs={24}>
        <CustomCard
          title={
            <Space style={{ justifyContent: 'space-between', width: '100%' }}>
              <Title level={5} style={{ fontWeight: '600', color: '#575962' }}>
                {t('advanced-informations.title')}
              </Title>
              <Button
                type="primary"
                onClick={() => handleTest()}
                disabled={!ableToGet}
              >
                {t('advanced-informations.search-button')}
              </Button>
            </Space>
          }
        >
          <Row gutter={[12, 12]}>
            <Col lg={8} xs={12}>
              <StatCard
                title={
                  <div style={{ display: 'flex' }}>
                    <Typography>VLR</Typography>
                    <Tooltip
                      title={t('advanced-informations.vlr-information.info')}
                    >
                      <Button
                        type="link"
                        size="small"
                        icon={<InfoCircleOutlined />}
                      />
                    </Tooltip>
                  </div>
                }
                icon={
                  <Badge
                    status={subscriberInfo?.vlrNum ? 'success' : 'error'}
                  />
                }
                value={
                  subscriberInfo?.vlrNum
                    ? t('advanced-informations.vlr-information.registered')
                    : t('advanced-informations.vlr-information.unregistered')
                }
                loading={!subscriberInfo || !ableToGet}
              />
            </Col>
            {((!subscriberInfo?.mmeInfo && subscriberInfo?.sgsnNum) ||
              (!subscriberInfo?.mmeInfo && !subscriberInfo?.sgsnNum)) && (
              <Col lg={8} xs={12}>
                <StatCard
                  title={
                    <div style={{ display: 'flex' }}>
                      <Typography>SGSN</Typography>
                      <Tooltip
                        title={t('advanced-informations.sgsn-information.info')}
                      >
                        <Button
                          type="link"
                          size="small"
                          icon={<InfoCircleOutlined />}
                        />
                      </Tooltip>
                    </div>
                  }
                  icon={
                    <Badge
                      status={subscriberInfo?.sgsnNum ? 'success' : 'error'}
                    />
                  }
                  value={
                    subscriberInfo?.sgsnNum
                      ? t('advanced-informations.sgsn-information.registered')
                      : t('advanced-informations.sgsn-information.unregistered')
                  }
                  loading={!subscriberInfo || !ableToGet}
                />
              </Col>
            )}

            {((!subscriberInfo?.sgsnNum && subscriberInfo?.mmeInfo) ||
              (!subscriberInfo?.sgsnNum && !subscriberInfo?.mmeInfo)) && (
              <Col lg={8} xs={12}>
                <StatCard
                  title={
                    <div style={{ display: 'flex' }}>
                      <Typography>MME</Typography>
                      <Tooltip
                        title={t('advanced-informations.mme-information.info')}
                      >
                        <Button
                          type="link"
                          size="small"
                          icon={<InfoCircleOutlined />}
                        />
                      </Tooltip>
                    </div>
                  }
                  icon={
                    <Badge
                      status={subscriberInfo?.mmeInfo ? 'success' : 'error'}
                    />
                  }
                  value={
                    subscriberInfo?.mmeInfo
                      ? t('advanced-informations.mme-information.registered')
                      : t('advanced-informations.mme-information.unregistered')
                  }
                  loading={!subscriberInfo || !ableToGet}
                />
              </Col>
            )}
          </Row>
          <Row gutter={[12, 12]} style={{ marginTop: 24 }}>
            <Col lg={11} xs={24}>
              <div
                style={{
                  height: 400,
                  overflow: 'auto',
                  padding: '0 16px',
                }}
              >
                <Typography style={{ fontWeight: '600', fontSize: 'xl' }}>
                  APNs
                </Typography>

                <List
                  className="apn-list"
                  loading={!subscriberInfo || !ableToGet}
                  itemLayout="horizontal"
                  size="large"
                  dataSource={subscriberInfo?.apns}
                  renderItem={(item: { apn: string }) => (
                    <List.Item>
                      <Skeleton loading={false}>
                        <List.Item.Meta description={item?.apn} />
                      </Skeleton>
                    </List.Item>
                  )}
                />
              </div>
            </Col>
            <Divider type="vertical" />
            <Col lg={12} xs={24}>
              <Typography style={{ fontWeight: '600', fontSize: 'xl' }}>
                cRR
              </Typography>

              {!subscriberInfo || !ableToGet ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '18px',
                  }}
                >
                  <Spin />
                </div>
              ) : (
                <>
                  {subscriberInfo?.blockReceiveCRR &&
                  subscriberInfo?.blockSendCRR ? (
                    <Space style={{ padding: '24px' }} direction="vertical">
                      <Typography style={{ fontSize: 'xl' }}>
                        <Badge
                          color={
                            subscriberInfo?.blockReceiveCRR ===
                            'BLOCK_TO_RECEIVE_SMS'
                              ? 'red'
                              : 'green'
                          }
                          text={t(
                            `advanced-informations.blockReceiveCRR.${subscriberInfo?.blockReceiveCRR}`,
                          )}
                          size="small"
                        />
                      </Typography>
                      <Typography style={{ fontSize: 'xl' }}>
                        <Badge
                          color={
                            subscriberInfo?.blockSendCRR === 'BLOCK_TO_SEND_SMS'
                              ? 'red'
                              : 'green'
                          }
                          text={t(
                            `advanced-informations.blockSendCRR.${subscriberInfo?.blockSendCRR}`,
                          )}
                          size="small"
                        />
                      </Typography>
                    </Space>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                </>
              )}
            </Col>
          </Row>
        </CustomCard>
      </Col>
    </ContainerRow>
  );
};

export default TabPanesAdvanced;
